import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authApi = {
  register: async (data) => {
    const response = await api.post('/auth/register', data);
    return response.data;
  },
  login: async (data) => {
    const response = await api.post('/auth/login', data);
    return response.data;
  },
  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

// Director APIs
export const directorsApi = {
  create: async (data) => {
    const response = await api.post('/directors', data);
    return response.data;
  },
  getAll: async () => {
    const response = await api.get('/directors');
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/directors/${id}`);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/directors/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/directors/${id}`);
    return response.data;
  },
  associate: async (companyId, directorId) => {
    const response = await api.post(`/companies/${companyId}/directors/${directorId}`);
    return response.data;
  },
  deassociate: async (companyId, directorId) => {
    const response = await api.delete(`/companies/${companyId}/directors/${directorId}`);
    return response.data;
  },
  setDscReminders: async (directorId, enabled) => {
    const response = await api.patch(`/directors/${directorId}/dsc-reminders`, { enabled });
    return response.data;
  },
};

// Company APIs
export const companiesApi = {
  create: async (data) => {
    const response = await api.post('/companies', data);
    return response.data;
  },
  getAll: async () => {
    const response = await api.get('/companies');
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/companies/${id}`);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/companies/${id}`, data);
    return response.data;
  },
  getDirectors: async (companyId) => {
    const response = await api.get(`/companies/${companyId}/directors`);
    return response.data;
  },
  addShareholder: async (companyId, data) => {
    const response = await api.post(`/companies/${companyId}/shareholders`, data);
    return response.data;
  },
  getShareholders: async (companyId) => {
    const response = await api.get(`/companies/${companyId}/shareholders`);
    return response.data;
  },
  updateShareholder: async (shareholderId, data) => {
    const response = await api.put(`/shareholders/${shareholderId}`, data);
    return response.data;
  },
  addDirector: async (companyId, directorId) => {
    const response = await api.post(`/companies/${companyId}/directors/${directorId}`);
    return response.data;
  },
};

// Product APIs
export const productsApi = {
  getAll: async (category = null) => {
    const params = category ? { category } : {};
    const response = await api.get('/products', { params });
    return response.data;
  },
  getAllAdmin: async (includeInactive = false) => {
    const response = await api.get('/admin/products', {
      params: { include_inactive: includeInactive }
    });
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/products/${id}`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/admin/products', data);
    return response.data;
  },
  update: async (productId, data) => {
    const response = await api.put(`/admin/products/${productId}`, data);
    return response.data;
  },
  delete: async (productId) => {
    const response = await api.delete(`/admin/products/${productId}`);
    return response.data;
  },
  hardDelete: async (productId) => {
    const response = await api.delete(`/admin/products/${productId}?hard=true`);
    return response.data;
  },
  restore: async (productId) => {
    const response = await api.put(`/admin/products/${productId}/restore`);
    return response.data;
  },
  getKycRequirements: async (productId) => {
    const response = await api.get(`/products/${productId}/kyc-requirements`);
    return response.data;
  },
  saveKycRequirements: async (productId, requirements) => {
    const response = await api.post(`/products/${productId}/kyc-requirements`, { requirements });
    return response.data;
  },
};

export const recurringPauseApi = {
  pause: async (companyId, productId, pauseReason) => {
    const response = await api.post('/admin/recurring-pause', { company_id: companyId, product_id: productId, pause_reason: pauseReason });
    return response.data;
  },
  resume: async (companyId, productId) => {
    const response = await api.post('/admin/recurring-resume', { company_id: companyId, product_id: productId });
    return response.data;
  },
  getStatus: async (companyId, productId) => {
    const response = await api.get(`/admin/recurring-pause/status?company_id=${companyId}&product_id=${productId}`);
    return response.data;
  },
};

export const dscConsentApi = {
  // Admin
  sendRequest: async (orderId, filingPurpose) => {
    const response = await api.post(`/admin/orders/${orderId}/dsc-consent`, { filing_purpose: filingPurpose });
    return response.data;
  },
  getHistory: async (orderId) => {
    const response = await api.get(`/admin/orders/${orderId}/dsc-consent-history`);
    return response.data;
  },
  // Client
  getPending: async () => {
    const response = await api.get('/dsc-consents/pending');
    return response.data;
  },
  getAll: async () => {
    const response = await api.get('/dsc-consents/all');
    return response.data;
  },
  getForOrder: async (orderId) => {
    const response = await api.get(`/orders/${orderId}/dsc-consent`);
    return response.data;
  },
  respond: async (consentId, action) => {
    const response = await api.put(`/dsc-consents/${consentId}/respond`, { action });
    return response.data;
  },
};

// Order APIs
export const ordersApi = {
  create: async (data) => {
    const response = await api.post('/orders', data);
    return response.data;
  },
  getAll: async (status = null) => {
    const params = status ? { status } : {};
    const response = await api.get('/orders', { params });
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/orders/${id}`);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/orders/${id}`, data);
    return response.data;
  },
  submitRequirement: async (orderId, data) => {
    const response = await api.post(`/orders/${orderId}/submissions`, data);
    return response.data;
  },
  uploadSignedDoc: async (orderId, docId, signedUrl) => {
    const response = await api.put(`/orders/${orderId}/signature-docs/${docId}/sign`, null, {
      params: { signed_url: signedUrl }
    });
    return response.data;
  },
  getKycGap: async (orderId, entityType = null, entityId = null) => {
    let url = `/orders/${orderId}/kyc-gap`;
    const params = [];
    if (entityType) params.push(`entity_type=${entityType}`);
    if (entityId) params.push(`entity_id=${entityId}`);
    if (params.length) url += '?' + params.join('&');
    const response = await api.get(url);
    return response.data;
  },
};

// Dashboard APIs
export const dashboardApi = {
  getStats: async () => {
    const response = await api.get('/dashboard/stats');
    return response.data;
  },
  getAdminStats: async () => {
    const response = await api.get('/admin/dashboard/stats');
    return response.data;
  },
  getActionRequired: async () => {
    const response = await api.get('/admin/action-required');
    return response.data;
  },
};

// Admin APIs
export const adminApi = {
  // Users
  updateUser: async (userId, data) => {
    const response = await api.put(`/admin/users/${userId}`, data);
    return response.data;
  },
  changeUserRole: async (userId, newRole) => {
    const response = await api.put(`/super-admin/users/${userId}/role`, { new_role: newRole });
    return response.data;
  },
  
  // Communication Preferences
  getCommunicationPreferences: async (userId) => {
    const response = await api.get(`/admin/users/${userId}/communication-preferences`);
    return response.data;
  },
  updateCommunicationPreferences: async (userId, preferences) => {
    const response = await api.put(`/admin/users/${userId}/communication-preferences`, preferences);
    return response.data;
  },
  getEmailRecipients: async (userId) => {
    const response = await api.get(`/admin/users/${userId}/email-recipients`);
    return response.data;
  },

  // Requirements
  createRequirement: async (data) => {
    const response = await api.post('/admin/requirements', data);
    return response.data;
  },
  getRequirements: async (category = null) => {
    const params = category ? { category } : {};
    const response = await api.get('/admin/requirements', { params });
    return response.data;
  },
  updateRequirement: async (id, data) => {
    const response = await api.put(`/admin/requirements/${id}`, data);
    return response.data;
  },
  
  // Products
  createProduct: async (data) => {
    const response = await api.post('/admin/products', data);
    return response.data;
  },
  updateProduct: async (id, data) => {
    const response = await api.put(`/admin/products/${id}`, data);
    return response.data;
  },
  linkRequirement: async (productId, reqId, isMandatory = true) => {
    const response = await api.post(
      `/admin/products/${productId}/requirements/${reqId}`,
      null,
      { params: { is_mandatory: isMandatory } }
    );
    return response.data;
  },
  unlinkRequirement: async (productId, reqId) => {
    const response = await api.delete(`/admin/products/${productId}/requirements/${reqId}`);
    return response.data;
  },
  
  // User management
  getUsers: async () => {
    const response = await api.get('/admin/users');
    return response.data;
  },
  getUserDetails: async (userId) => {
    const response = await api.get(`/admin/users/${userId}`);
    return response.data;
  },
  
  // Company management
  getCompanies: async () => {
    const response = await api.get('/admin/companies');
    return response.data;
  },
  getCompanyDetails: async (companyId) => {
    const response = await api.get(`/admin/companies/${companyId}`);
    return response.data;
  },
  updateCompany: async (companyId, data) => {
    const response = await api.put(`/admin/companies/${companyId}`, data);
    return response.data;
  },
  getCompanyDirectors: async (companyId) => {
    const response = await api.get(`/companies/${companyId}/directors`);
    return response.data;
  },
  getCompanyShareholders: async (companyId) => {
    const response = await api.get(`/companies/${companyId}/shareholders`);
    return response.data;
  },
  getCompanyOrders: async (companyId) => {
    const response = await api.get(`/companies/${companyId}/orders`);
    return response.data;
  },
  getDirectorCompanies: async (directorId) => {
    const response = await api.get(`/directors/${directorId}/companies`);
    return response.data;
  },
  getShareholderCompany: async (shareholderId) => {
    const response = await api.get(`/shareholders/${shareholderId}/company`);
    return response.data;
  },
  getUserCompanies: async (userId) => {
    const response = await api.get(`/admin/users/${userId}/companies`);
    return response.data;
  },
  getUserExpandedData: async (userId) => {
    const response = await api.get(`/admin/users/${userId}/expanded-data`);
    return response.data;
  },
  getCompanyActiveServices: async (companyId) => {
    const response = await api.get(`/service-expiry/company/${companyId}/active-services`);
    return response.data;
  },
  getExpiryTrackerAll: async () => {
    const response = await api.get('/expiry-tracker/all');
    return response.data;
  },
  recommendProduct: async (companyId, productId) => {
    const response = await api.post(
      `/admin/companies/${companyId}/recommend-product`,
      null,
      { params: { product_id: productId } }
    );
    return response.data;
  },
  assignMultipleProducts: async (companyId, productIds) => {
    const response = await api.post(
      `/admin/companies/${companyId}/assign-products`,
      productIds
    );
    return response.data;
  },
  
  // Director management
  getDirectors: async () => {
    const response = await api.get('/admin/directors');
    return response.data;
  },
  getDirectorDetails: async (directorId) => {
    const response = await api.get(`/admin/directors/${directorId}`);
    return response.data;
  },
  updateDirector: async (directorId, data) => {
    const response = await api.put(`/admin/directors/${directorId}`, data);
    return response.data;
  },
  
  // Shareholder management
  getShareholders: async () => {
    const response = await api.get('/admin/shareholders');
    return response.data;
  },
  getShareholderDetails: async (shareholderId) => {
    const response = await api.get(`/admin/shareholders/${shareholderId}`);
    return response.data;
  },
  updateShareholder: async (shareholderId, data) => {
    const response = await api.put(`/admin/shareholders/${shareholderId}`, data);
    return response.data;
  },
  
  // Order management
  getOrders: async (status = null) => {
    const params = status ? { status } : {};
    const response = await api.get('/admin/orders', { params });
    return response.data;
  },
  getOrderDetails: async (orderId) => {
    const response = await api.get(`/admin/orders/${orderId}`);
    return response.data;
  },
  updateOrderStatus: async (orderId, status, notes = null) => {
    const params = { status };
    if (notes) params.notes = notes;
    const response = await api.put(`/admin/orders/${orderId}/status`, null, { params });
    return response.data;
  },
  // Government Filing Status
  updateGovtFilingStatus: async (orderId, formData) => {
    const response = await api.put(`/admin/orders/${orderId}/govt-filing-status`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
  getGovtFilingStatus: async (orderId) => {
    const response = await api.get(`/admin/orders/${orderId}/govt-filing-status`);
    return response.data;
  },
  syncOrderToAsana: async (orderId) => {
    const response = await api.post(`/admin/orders/${orderId}/sync-asana`);
    return response.data;
  },
  createPaymentLink: async (orderId) => {
    const response = await api.post(`/admin/orders/${orderId}/payment-link`);
    return response.data;
  },
  getPaymentLink: async (orderId) => {
    const response = await api.get(`/admin/orders/${orderId}/payment-link`);
    return response.data;
  },
  applyDiscount: async (orderId, discountPercent) => {
    const response = await api.put(`/admin/orders/${orderId}/discount`, { discount_percent: discountPercent });
    return response.data;
  },
  listInvoices: async (params = {}) => {
    const response = await api.get('/admin/invoices', { params });
    return response.data;
  },
  reviewSubmission: async (orderId, submissionId, data) => {
    const response = await api.put(`/admin/orders/${orderId}/submissions/${submissionId}`, data);
    return response.data;
  },
  approveSubmission: async (orderId, submissionId) => {
    const response = await api.put(
      `/admin/orders/${orderId}/submissions/${submissionId}/review`,
      null,
      { params: { status: 'approved' } }
    );
    return response.data;
  },
  rejectSubmission: async (orderId, submissionId, comment) => {
    const response = await api.put(
      `/admin/orders/${orderId}/submissions/${submissionId}/review`,
      null,
      { params: { status: 'rejected', comment } }
    );
    return response.data;
  },
  // Signature document management
  getSignatureDocuments: async (orderId) => {
    const response = await api.get(`/admin/orders/${orderId}/signature-documents`);
    return response.data;
  },
  uploadSignatureDocument: async (orderId, caption, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(
      `/admin/orders/${orderId}/signature-documents?caption=${encodeURIComponent(caption)}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },
  deleteSignatureDocument: async (orderId, docId) => {
    const response = await api.delete(`/admin/orders/${orderId}/signature-documents/${docId}`);
    return response.data;
  },
  approveSignedDocument: async (orderId, docId) => {
    const response = await api.put(
      `/admin/orders/${orderId}/signature-documents/${docId}/review`,
      null,
      { params: { status: 'approved' } }
    );
    return response.data;
  },
  rejectSignedDocument: async (orderId, docId, comment) => {
    const response = await api.put(
      `/admin/orders/${orderId}/signature-documents/${docId}/review`,
      null,
      { params: { status: 'rejected', comment } }
    );
    return response.data;
  },
  uploadSignatureDoc: async (orderId, docName, docUrl) => {
    const response = await api.post(`/admin/orders/${orderId}/signature-docs`, null, {
      params: { document_name: docName, document_url: docUrl }
    });
    return response.data;
  },
  
  // Acknowledgement document management
  getAcknowledgements: async (orderId) => {
    const response = await api.get(`/admin/orders/${orderId}/acknowledgements`);
    return response.data;
  },
  uploadAcknowledgement: async (orderId, documentName, file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_name', documentName);
    const response = await api.post(
      `/admin/orders/${orderId}/acknowledgements`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },
  deleteAcknowledgement: async (orderId, docId) => {
    const response = await api.delete(`/admin/orders/${orderId}/acknowledgements/${docId}`);
    return response.data;
  },
  completeOrder: async (orderId) => {
    const response = await api.post(`/admin/orders/${orderId}/complete`);
    return response.data;
  },
  
  // Invoice-based payment flow
  sendInvoice: async (orderId) => {
    const response = await api.post(`/admin/orders/${orderId}/send-invoice`);
    return response.data;
  },
  markPaymentReceived: async (orderId, paymentReference = null) => {
    const params = paymentReference ? { payment_reference: paymentReference } : {};
    const response = await api.post(`/admin/orders/${orderId}/mark-payment-received`, null, { params });
    return response.data;
  },
  activateDocuments: async (orderId) => {
    const response = await api.post(`/admin/orders/${orderId}/activate-documents`);
    return response.data;
  },
  // Mock payment for manual/offline payments (Super Admin only)
  mockPayment: async (orderId, remark = null) => {
    const params = {};
    if (remark) params.remark = remark;
    const response = await api.post(`/admin/orders/${orderId}/mock-payment`, null, { params });
    return response.data;
  },

  // Internal Notes
  getOrderNotes: async (orderId) => {
    const response = await api.get(`/orders/${orderId}/notes`);
    return response.data;
  },
  createOrderNote: async (orderId, content, file = null) => {
    const formData = new FormData();
    formData.append('content', content);
    if (file) formData.append('file', file);
    const response = await api.post(`/orders/${orderId}/notes`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  // Activity Log
  getOrderActivity: async (orderId) => {
    const response = await api.get(`/orders/${orderId}/activity`);
    return response.data;
  },

  // Filing Status (for recurring/filing products)
  updateFilingStatus: async (orderId, data) => {
    const response = await api.put(`/filing-status/${orderId}`, data);
    return response.data;
  },
  clientFilingAction: async (orderId, action, clientRemark) => {
    const response = await api.put(`/filing-status/${orderId}/client-action`, {
      action,
      client_remark: clientRemark,
    });
    return response.data;
  },
  
  // Order assignment
  assignOrder: async (orderId, adminId) => {
    const params = adminId ? { admin_id: adminId } : {};
    const response = await api.post(`/admin/orders/${orderId}/assign`, null, { params });
    return response.data;
  },
  
  // Admin profile management
  updateAdminProfile: async (data) => {
    const response = await api.put('/admin/profile', data);
    return response.data;
  },
  uploadAdminDocument: async (documentType, file) => {
    const formData = new FormData();
    formData.append('document_type', documentType);
    formData.append('file', file);
    const response = await api.post('/admin/profile/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
  deleteAdminDocument: async (docId) => {
    const response = await api.delete(`/admin/profile/documents/${docId}`);
    return response.data;
  },
  
  // Recurring Orders Management
  getRecurringSummary: async () => {
    const response = await api.get('/admin/recurring/summary');
    return response.data;
  },
  getRecurringStatus: async (productId) => {
    const response = await api.get(`/admin/recurring/status/${productId}`);
    return response.data;
  },
  generateRecurringOrders: async (productId, force = false) => {
    const response = await api.post(`/admin/recurring/generate/${productId}?force=${force}`);
    return response.data;
  },
  generateAllRecurringOrders: async () => {
    const response = await api.post('/admin/recurring/generate-all');
    return response.data;
  },
  getRecurringLog: async (productId = null, limit = 50) => {
    const params = { limit };
    if (productId) params.product_id = productId;
    const response = await api.get('/admin/recurring/log', { params });
    return response.data;
  },
  getRenewalSummary: async () => {
    const response = await api.get('/admin/recurring/renewals');
    return response.data;
  },
  triggerRenewalCheck: async () => {
    const response = await api.post('/admin/recurring/trigger-renewal-check');
    return response.data;
  },

  // 3-Tab Order Views
  getOneTimeOrders: async (params = {}) => {
    const response = await api.get('/admin/orders/one-time', { params });
    return response.data;
  },
  getYearlyOrders: async (params = {}) => {
    const response = await api.get('/admin/orders/yearly', { params });
    return response.data;
  },
  getCustomOrders: async (params = {}) => {
    const response = await api.get('/admin/orders/custom', { params });
    return response.data;
  },
  getMonthlyOrders: async (params = {}) => {
    const response = await api.get('/admin/orders/monthly', { params });
    return response.data;
  },
  getQuarterlyOrders: async (params = {}) => {
    const response = await api.get('/admin/orders/quarterly', { params });
    return response.data;
  },
  // Service-type-based order views
  getOrdersByService: async (serviceType, params = {}) => {
    const response = await api.get(`/admin/orders/by-service/${serviceType}`, { params });
    return response.data;
  },
  getRenewalHistory: async (orderId) => {
    const response = await api.get(`/admin/orders/${orderId}/renewal-history`);
    return response.data;
  },

  // Scheduler
  getSchedulerLogs: async (page = 1, limit = 20) => {
    const response = await api.get('/admin/scheduler/logs', { params: { page, limit } });
    return response.data;
  },
  getLatestSchedulerRun: async () => {
    const response = await api.get('/admin/scheduler/latest');
    return response.data;
  },
  triggerYearlyOrders: async (year) => {
    const response = await api.post('/super-admin/scheduler/trigger-yearly', { year });
    return response.data;
  },
  triggerCustomOrders: async () => {
    const response = await api.post('/super-admin/scheduler/trigger-custom');
    return response.data;
  },
  triggerReminders: async () => {
    const response = await api.post('/super-admin/scheduler/trigger-reminders');
    return response.data;
  },
  triggerMonthlyOrders: async () => {
    const response = await api.post('/super-admin/scheduler/trigger-monthly');
    return response.data;
  },
  triggerQuarterlyOrders: async () => {
    const response = await api.post('/super-admin/scheduler/trigger-quarterly');
    return response.data;
  },

  // Reminder management
  togglePauseReminders: async (orderId) => {
    const response = await api.put(`/admin/orders/${orderId}/pause-reminders`);
    return response.data;
  },
  overrideDocLastDate: async (orderId, day, month) => {
    const response = await api.put(`/admin/orders/${orderId}/override-last-date`, { day, month });
    return response.data;
  },
  getReminderLog: async (orderId) => {
    const response = await api.get(`/admin/orders/${orderId}/reminder-log`);
    return response.data;
  },
};

// KYC Document APIs
export const kycApi = {
  // Company KYC
  getCompanyDocuments: async (companyId) => {
    const response = await api.get(`/companies/${companyId}/kyc-documents`);
    return response.data;
  },
  uploadCompanyDocument: async (companyId, documentType, documentName, description, file) => {
    const formData = new FormData();
    formData.append('document_type', documentType);
    formData.append('document_name', documentName);
    if (description) formData.append('description', description);
    formData.append('file', file);
    const response = await api.post(`/companies/${companyId}/kyc-documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
  
  // Director KYC
  getDirectorDocuments: async (directorId) => {
    const response = await api.get(`/directors/${directorId}/kyc-documents`);
    return response.data;
  },
  uploadDirectorDocument: async (directorId, documentType, documentName, description, file) => {
    const formData = new FormData();
    formData.append('document_type', documentType);
    formData.append('document_name', documentName);
    if (description) formData.append('description', description);
    formData.append('file', file);
    const response = await api.post(`/directors/${directorId}/kyc-documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
  
  // Shareholder KYC
  getShareholderDocuments: async (shareholderId) => {
    const response = await api.get(`/shareholders/${shareholderId}/kyc-documents`);
    return response.data;
  },
  uploadShareholderDocument: async (shareholderId, documentType, documentName, description, file) => {
    const formData = new FormData();
    formData.append('document_type', documentType);
    formData.append('document_name', documentName);
    if (description) formData.append('description', description);
    formData.append('file', file);
    const response = await api.post(`/shareholders/${shareholderId}/kyc-documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
  
  // Admin only - Delete & Verify
  deleteDocument: async (documentId) => {
    const response = await api.delete(`/kyc-documents/${documentId}`);
    return response.data;
  },
  verifyDocument: async (documentId, status, notes = null) => {
    const response = await api.put(`/kyc-documents/${documentId}/verify`, null, {
      params: { status, notes }
    });
    return response.data;
  },
  
  // Get presigned download URL for a document
  getDownloadUrl: async (documentId) => {
    const response = await api.get(`/kyc/download-url/${documentId}`);
    return response.data;
  },
};

// Client Order APIs
export const myOrdersApi = {
  getAll: async () => {
    const response = await api.get('/my-orders');
    return response.data;
  },
  getById: async (orderId) => {
    const response = await api.get(`/my-orders/${orderId}`);
    return response.data;
  },
  requestInvoice: async (orderId) => {
    const response = await api.post(`/orders/${orderId}/request-invoice`);
    return response.data;
  },
  uploadDocument: async (orderId, requirementId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(
      `/orders/${orderId}/upload`,
      formData,
      { 
        params: { requirement_id: requirementId },
        headers: { 'Content-Type': 'multipart/form-data' }
      }
    );
    return response.data;
  },
  submitText: async (orderId, requirementId, value) => {
    const response = await api.post(
      `/orders/${orderId}/submit-text`,
      { requirement_id: requirementId, value }
    );
    return response.data;
  },
  submitDocuments: async (orderId) => {
    const response = await api.post(`/orders/${orderId}/submit-documents`);
    return response.data;
  },
  deleteSubmission: async (orderId, submissionId) => {
    const response = await api.delete(`/orders/${orderId}/submissions/${submissionId}`);
    return response.data;
  },
  // Signature document APIs
  getSignatureDocuments: async (orderId) => {
    const response = await api.get(`/orders/${orderId}/signature-documents`);
    return response.data;
  },
  uploadSignedDocument: async (orderId, docId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(
      `/orders/${orderId}/signature-documents/${docId}/upload-signed`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },
  approveRejectSignatureDoc: async (orderId, docId, action, remark = '') => {
    const response = await api.post(
      `/orders/${orderId}/signature-documents/${docId}/client-action`,
      { action, client_remark: remark }
    );
    return response.data;
  },
  submitSignatures: async (orderId) => {
    const response = await api.post(`/orders/${orderId}/submit-signatures`);
    return response.data;
  },
};

// Landing page APIs (existing)
export const servicesApi = {
  getAll: async () => {
    const response = await api.get('/services');
    return response.data;
  },
};

export const faqsApi = {
  getAll: async () => {
    const response = await api.get('/faqs');
    return response.data;
  },
  getCategories: async () => {
    const response = await api.get('/faqs/categories');
    return response.data;
  },
};

export const misconceptionsApi = {
  getAll: async () => {
    const response = await api.get('/misconceptions');
    return response.data;
  },
};

export const inquiriesApi = {
  submit: async (data) => {
    const response = await api.post('/inquiries', data);
    return response.data;
  },
};

export const leadsApi = {
  list: async (status) => {
    const response = await api.get('/admin/leads', { params: status ? { status } : {} });
    return response.data;
  },
  updateStatus: async (id, status) => {
    const response = await api.patch(`/admin/leads/${id}`, { status });
    return response.data;
  },
};

// Document Types API (Product Engine)
export const documentTypesApi = {
  getAll: async () => {
    const response = await api.get('/document-types');
    return response.data;
  },
  getAllAdmin: async () => {
    const response = await api.get('/admin/document-types');
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/admin/document-types', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/admin/document-types/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/admin/document-types/${id}`);
    return response.data;
  },
};

// Information Fields API (Product Engine)
export const informationFieldsApi = {
  getAll: async () => {
    const response = await api.get('/information-fields');
    return response.data;
  },
  getAllAdmin: async () => {
    const response = await api.get('/admin/information-fields');
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/admin/information-fields', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/admin/information-fields/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/admin/information-fields/${id}`);
    return response.data;
  },
};

// Invoices API
export const invoicesApi = {
  // Client methods
  getMyInvoices: async () => {
    const response = await api.get('/invoices');
    return response.data;
  },
  getInvoice: async (invoiceId) => {
    const response = await api.get(`/invoices/${invoiceId}`);
    return response.data;
  },
  getOrderInvoice: async (orderId) => {
    const response = await api.get(`/orders/${orderId}/invoice`);
    return response.data;
  },
  uploadPaymentProof: async (invoiceId, file, paymentMode = 'bank_transfer', paymentReference = null) => {
    const formData = new FormData();
    formData.append('file', file);
    const params = new URLSearchParams();
    params.append('payment_mode', paymentMode);
    if (paymentReference) params.append('payment_reference', paymentReference);
    const response = await api.post(
      `/invoices/${invoiceId}/upload-payment-proof?${params.toString()}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },
  
  // Admin methods
  getAllAdmin: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.company_id) params.append('company_id', filters.company_id);
    const response = await api.get(`/admin/invoices?${params.toString()}`);
    return response.data;
  },
  getSummary: async () => {
    const response = await api.get('/admin/invoices/summary');
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/admin/invoices', data);
    return response.data;
  },
  update: async (invoiceId, data) => {
    const response = await api.put(`/admin/invoices/${invoiceId}`, data);
    return response.data;
  },
  sendInvoice: async (invoiceId, lineItems, dueDays = 7) => {
    const params = new URLSearchParams();
    params.append('due_days', dueDays);
    const response = await api.post(`/admin/invoices/${invoiceId}/send?${params.toString()}`, lineItems);
    return response.data;
  },
  markPaid: async (invoiceId, paymentMode, paymentReference = null, paymentDate = null) => {
    const params = new URLSearchParams();
    params.append('payment_mode', paymentMode);
    if (paymentReference) params.append('payment_reference', paymentReference);
    if (paymentDate) params.append('payment_date', paymentDate);
    const response = await api.post(`/admin/invoices/${invoiceId}/mark-paid?${params.toString()}`);
    return response.data;
  },
  approvePayment: async (invoiceId) => {
    const response = await api.post(`/admin/invoices/${invoiceId}/approve-payment`);
    return response.data;
  },
  rejectPayment: async (invoiceId, reason) => {
    const params = new URLSearchParams();
    params.append('reason', reason);
    const response = await api.post(`/admin/invoices/${invoiceId}/reject-payment?${params.toString()}`);
    return response.data;
  },
  sendReminder: async (invoiceId) => {
    const response = await api.post(`/admin/invoices/${invoiceId}/send-reminder`);
    return response.data;
  },
  cancel: async (invoiceId) => {
    const response = await api.delete(`/admin/invoices/${invoiceId}`);
    return response.data;
  },
};

// Payments API - Bank Transfer Payment Flow
export const paymentsApi = {
  // Submit payment confirmation (user clicked "I have Paid")
  submitPaymentConfirmation: async (orderId) => {
    const response = await api.post(`/orders/${orderId}/submit-payment-confirmation`);
    return response.data;
  },
  
  // Get payment status for an order
  getPaymentStatus: async (orderId) => {
    const response = await api.get(`/orders/${orderId}/payment-status`);
    return response.data;
  },
  
  // Admin: Mark order as paid (mock payment)
  adminMockPayment: async (orderId, paymentReference = null) => {
    const params = paymentReference ? { payment_reference: paymentReference } : {};
    const response = await api.post(`/admin/orders/${orderId}/mock-payment`, null, { params });
    return response.data;
  },
  
  // Create Razorpay payment order
  createPaymentOrder: async (orderId) => {
    const response = await api.post(`/orders/${orderId}/create-payment`);
    return response.data;
  },
  
  // Verify Razorpay payment
  verifyPayment: async (orderId, razorpayPaymentId, razorpayOrderId, razorpaySignature) => {
    const response = await api.post(`/orders/${orderId}/verify-payment`, null, {
      params: {
        razorpay_payment_id: razorpayPaymentId,
        razorpay_order_id: razorpayOrderId,
        razorpay_signature: razorpaySignature
      }
    });
    return response.data;
  },

  // GST Post-confirmation payment
  createGstPayment: async (orderId) => {
    const response = await api.post(`/orders/${orderId}/gst/create-payment`);
    return response.data;
  },
  verifyGstPayment: async (orderId, razorpayPaymentId, razorpayOrderId, razorpaySignature) => {
    const response = await api.post(`/orders/${orderId}/gst/verify-payment`, null, {
      params: {
        razorpay_payment_id: razorpayPaymentId,
        razorpay_order_id: razorpayOrderId,
        razorpay_signature: razorpaySignature
      }
    });
    return response.data;
  },
  
  // Create Stripe checkout session (US payments)
  createStripeCheckout: async (orderId) => {
    const originUrl = window.location.origin;
    const response = await api.post('/payments/stripe/checkout', {
      order_id: orderId,
      origin_url: originUrl
    });
    return response.data;
  },
  
  // Get Stripe payment status
  getStripePaymentStatus: async (sessionId) => {
    const response = await api.get(`/payments/stripe/status/${sessionId}`);
    return response.data;
  },
};

// Super Admin API - Discount & Advanced Management
export const superAdminApi = {
  // Apply discount to order
  applyDiscount: async (orderId, discountPercentage, reason = null) => {
    const params = new URLSearchParams();
    params.append('discount_percentage', discountPercentage);
    if (reason) params.append('reason', reason);
    const response = await api.post(`/super-admin/orders/${orderId}/discount?${params.toString()}`);
    return response.data;
  },
  
  // Remove discount from order
  removeDiscount: async (orderId) => {
    const response = await api.delete(`/super-admin/orders/${orderId}/discount`);
    return response.data;
  },
  
  // Get all users (for super admin management)
  getUsers: async () => {
    const response = await api.get('/super-admin/users');
    return response.data;
  },
  
  // Delete user with cascade
  deleteUser: async (userId, confirm = true, reason = null) => {
    const response = await api.delete(`/super-admin/users/${userId}`, {
      data: { confirm, reason }
    });
    return response.data;
  },
  
  // Reassign company
  reassignCompany: async (companyId, newOwnerUserId, reason = null) => {
    const response = await api.post('/super-admin/reassign-company', {
      company_id: companyId,
      new_owner_user_id: newOwnerUserId,
      reason
    });
    return response.data;
  },
  
  // Get audit logs
  getAuditLogs: async (limit = 100, skip = 0) => {
    const response = await api.get(`/super-admin/audit-logs?limit=${limit}&skip=${skip}`);
    return response.data;
  },
  
  // Get file access logs
  getFileAccessLogs: async (limit = 100, skip = 0, filters = {}) => {
    const params = new URLSearchParams();
    params.append('limit', limit);
    params.append('skip', skip);
    if (filters.user_id) params.append('user_id', filters.user_id);
    if (filters.entity_type) params.append('entity_type', filters.entity_type);
    if (filters.action) params.append('action', filters.action);
    const response = await api.get(`/super-admin/file-access-logs?${params.toString()}`);
    return response.data;
  },
  
  // Revenue analytics
  getRevenueSummary: async () => {
    const response = await api.get('/super-admin/revenue/summary');
    return response.data;
  },
  
  getRevenueByMonth: async (months = 12) => {
    const response = await api.get(`/super-admin/revenue/by-month?months=${months}`);
    return response.data;
  },
  
  getRevenueByQuarter: async (year = null) => {
    const params = year ? `?year=${year}` : '';
    const response = await api.get(`/super-admin/revenue/by-quarter${params}`);
    return response.data;
  },
  
  getRevenueByProduct: async () => {
    const response = await api.get('/super-admin/revenue/by-product');
    return response.data;
  },
  
  getRevenueByUser: async () => {
    const response = await api.get('/super-admin/revenue/by-user');
    return response.data;
  },
  
  getRevenueByCompany: async () => {
    const response = await api.get('/super-admin/revenue/by-company');
    return response.data;
  },

  getSupportFooter: async () => {
    const response = await api.get('/super-admin/support-footer');
    return response.data;
  },

  updateSupportFooter: async (data) => {
    const response = await api.put('/super-admin/support-footer', data);
    return response.data;
  },
};

// Notifications API
export const notificationsApi = {
  // Get user notifications
  getNotifications: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.skip) queryParams.append('skip', params.skip);
    if (params.unread_only) queryParams.append('unread_only', 'true');
    if (params.action_required) queryParams.append('action_required', 'true');
    const response = await api.get(`/notifications?${queryParams.toString()}`);
    return response.data;
  },
  
  // Get notification summary (for badge counts)
  getSummary: async () => {
    const response = await api.get('/notifications/summary');
    return response.data;
  },
  
  // Mark notification as read
  markAsRead: async (notificationId) => {
    const response = await api.put(`/notifications/${notificationId}/read`);
    return response.data;
  },
  
  // Mark all as read
  markAllAsRead: async () => {
    const response = await api.put('/notifications/read-all');
    return response.data;
  },
  
  // Mark action complete
  markActionComplete: async (notificationId) => {
    const response = await api.put(`/notifications/${notificationId}/action-complete`);
    return response.data;
  },
  
  // Dismiss notification
  dismiss: async (notificationId) => {
    const response = await api.delete(`/notifications/${notificationId}`);
    return response.data;
  },
  
  // Dismiss all read
  dismissAll: async () => {
    const response = await api.delete('/notifications/dismiss-all');
    return response.data;
  },
};

// Config API - Country/State configuration for multi-country support
export const configApi = {
  // Get supported countries
  getCountries: async () => {
    const response = await api.get('/config/countries');
    return response.data;
  },
  
  // Get US states with fees
  getUSStates: async () => {
    const response = await api.get('/config/us/states');
    return response.data;
  },
  
  // Get specific US state details
  getUSStateDetails: async (stateCode) => {
    const response = await api.get(`/config/us/states/${stateCode}`);
    return response.data;
  },
  
  // Get US state fees for specific entity type
  getUSStateFees: async (stateCode, entityType = 'us_llc') => {
    const response = await api.get(`/config/us/states/${stateCode}/fees?entity_type=${entityType}`);
    return response.data;
  },
  
  // Get US entity types
  getUSEntityTypes: async () => {
    const response = await api.get('/config/us/entity-types');
    return response.data;
  },
  
  // Get India entity types
  getIndiaEntityTypes: async () => {
    const response = await api.get('/config/in/entity-types');
    return response.data;
  },
  
  // Get India states
  getIndiaStates: async () => {
    const response = await api.get('/config/in/states');
    return response.data;
  },
  
  // Get KYC document types by country
  getKYCDocumentTypes: async (country = 'IN') => {
    const response = await api.get(`/config/kyc-document-types?country=${country}`);
    return response.data;
  },
};

export default api;

// Activity Feed API (Orders Dashboard v2)
export const activityFeedApi = {
  getFeed: async (params = {}, options = {}) => {
    const query = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([_, v]) => v != null && v !== ''))
    ).toString();
    const response = await api.get(`/admin/activity-feed?${query}`, { signal: options.signal });
    return response.data;
  },
  getTeamMembers: async () => {
    const response = await api.get('/admin/activity-feed/team-members');
    return response.data;
  },
  getProducts: async () => {
    const response = await api.get('/admin/activity-feed/products');
    return response.data;
  },
  getOrdersGrouped: async (params = {}, options = {}) => {
    const query = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([_, v]) => v != null && v !== ''))
    ).toString();
    const response = await api.get(`/admin/orders-grouped?${query}`, { signal: options.signal });
    return response.data;
  },
  getReviewAlerts: async () => {
    const response = await api.get('/admin/activity-feed/review-alerts');
    return response.data;
  },
};


// ==================== Support Tickets ====================
export const ticketsApi = {
  list: async (params = {}) => {
    const query = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([_k, v]) => v != null && v !== ''))
    ).toString();
    const res = await api.get(`/tickets${query ? `?${query}` : ''}`);
    return res.data;
  },
  get: async (id) => {
    const res = await api.get(`/tickets/${id}`);
    return res.data;
  },
  create: async (payload) => {
    const res = await api.post('/tickets', payload);
    return res.data;
  },
  reply: async (id, text, visibility = 'public') => {
    const res = await api.post(`/tickets/${id}/reply`, { text, visibility });
    return res.data;
  },
  changeStatus: async (id, status) => {
    const res = await api.patch(`/tickets/${id}/status`, { status });
    return res.data;
  },
  assign: async (id, assigned_admin_id) => {
    const res = await api.patch(`/tickets/${id}/assign`, { assigned_admin_id });
    return res.data;
  },
  linkOrder: async (id, order_id) => {
    const res = await api.patch(`/tickets/${id}/link-order`, { order_id });
    return res.data;
  },
  setBlockedBy: async (id, order_id) => {
    const res = await api.patch(`/tickets/${id}/blocked-by`, { order_id });
    return res.data;
  },
  setDueDate: async (id, due_date) => {
    const res = await api.patch(`/tickets/${id}/due-date`, { due_date });
    return res.data;
  },
  deleteMessage: async (id, index) => {
    const res = await api.delete(`/tickets/${id}/messages/${index}`);
    return res.data;
  },
  listAdmins: async () => {
    const res = await api.get('/tickets/meta/admins');
    return res.data;
  },
  listCompanyOrders: async (companyId) => {
    const res = await api.get(`/tickets/meta/company/${companyId}/orders`);
    return res.data;
  },
};
